/**
 * DealCouponModal — web port of `apps/WunderGuide/components/deals/DealCouponModal.tsx`.
 *
 * Behaviour: tap a deal → modal slides up over the Deals overlay.
 *   • For real deals (UUID id): GET /deals/v1/{id}/user-claim → lazy-claim a
 *     code if the user can; render QR (via /utils/v1/qr) + the code text +
 *     copy button.
 *   • For mock deals (non-UUID id): just show the deal info + ends_at, with
 *     a "Save for later" CTA.
 *
 * Mirrors RN's display priority. Web doesn't ship the WebView fallback yet —
 * presentation.url/html is rendered as an iframe when available.
 */
import React, { useCallback, useEffect, useState } from 'react';
import dealsApi, { formatDiscount } from '../../api/dealsApi';
import type { DealSummary } from '@wg/types';
import config from '../../../config/env.json';
import Spinner from '../../common/Spinner';
import WebView from '../../common/WebView';
import './DealCouponModal.css';

interface DealCouponModalProps {
  deal: DealSummary | null;
  onClose: () => void;
}

const UUID_RX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const DealCouponModal: React.FC<DealCouponModalProps> = ({ deal, onClose }) => {
  const [claim, setClaim] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!deal) {
      setClaim(null);
      setError(null);
      return;
    }
    if (!UUID_RX.test(deal.id)) {
      // Mock deal — no claim flow.
      setClaim(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        let data = await dealsApi.getDealWithUserClaim(deal.id);
        if (!data?.user_claim?.code && data?.user_claim?.can_claim) {
          const claimResult = await dealsApi.claimDealCode(deal.id);
          if (claimResult?.success !== false) {
            data = await dealsApi.getDealWithUserClaim(deal.id);
          }
        }
        if (!cancelled) setClaim(data);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setClaim(null);
        } else {
          setError('Failed to load coupon.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deal]);

  const code = claim?.user_claim?.code ?? null;
  const presentationUrl = claim?.presentation?.url ?? null;
  const presentationHtml = claim?.presentation?.html ?? null;

  const onCopy = useCallback(async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }, [code]);

  if (!deal) return null;

  const discount = formatDiscount(deal.deal_type, deal.discount_value);
  const endsAt = deal.ends_at ? new Date(deal.ends_at) : null;
  const expiresIn = endsAt ? formatExpiresIn(endsAt) : null;

  const qrSrc = code
    ? `${config.BASE_URL}/utils/v1/qr?data=${encodeURIComponent(code)}&size=220&format=png&dark=%23000000&light=%23ffffff`
    : null;

  return (
    <div className="wg-coupon" role="dialog" aria-modal="true">
      <button
        type="button"
        className="wg-coupon__backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="wg-coupon__sheet">
        <button
          type="button"
          className="wg-coupon__close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        {loading && (
          <div className="wg-coupon__center">
            <Spinner size="medium" />
          </div>
        )}

        {!loading && error && (
          <div className="wg-coupon__error">
            <AlertIcon />
            <div>{error}</div>
            <button type="button" className="wg-coupon__cta" onClick={onClose}>
              Close
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* WebView equivalent — embed presentation if available */}
            {(presentationUrl || presentationHtml) && (
              <div className="wg-coupon__embed">
                <WebView
                  title="Coupon"
                  source={{
                    uri: presentationUrl ?? undefined,
                    html: presentationHtml ?? undefined,
                  }}
                />
              </div>
            )}

            {/* Native coupon: QR + code + merchant + description */}
            {!presentationUrl && !presentationHtml && (
              <>
                <div className="wg-coupon__code-card">
                  {qrSrc ? (
                    <img className="wg-coupon__qr" src={qrSrc} alt="QR" />
                  ) : (
                    <div className="wg-coupon__qr-placeholder">
                      <TagIcon />
                      <span>Show this to the merchant</span>
                    </div>
                  )}
                  {code && (
                    <button
                      type="button"
                      className="wg-coupon__code"
                      onClick={onCopy}
                      title="Copy code"
                    >
                      <span className="wg-coupon__code-text">{code}</span>
                      {copied ? <CheckIcon /> : <CopyIcon />}
                    </button>
                  )}
                  {copied && <div className="wg-coupon__copied">Copied!</div>}
                </div>

                {discount && (
                  <div className="wg-coupon__discount">{discount}</div>
                )}

                <div className="wg-coupon__merchant">
                  <span className="wg-coupon__merchant-logo">
                    {deal.owner_logo ? (
                      <img src={deal.owner_logo} alt="" />
                    ) : (
                      <StoreIcon />
                    )}
                  </span>
                  <span className="wg-coupon__merchant-name">
                    {deal.owner_name || 'Merchant'}
                  </span>
                </div>

                <h2 className="wg-coupon__title">{deal.title}</h2>

                {deal.description && (
                  <p className="wg-coupon__desc">{deal.description}</p>
                )}

                {expiresIn && (
                  <div className="wg-coupon__expiry">
                    <ClockIcon />
                    <span>Expires {expiresIn}</span>
                  </div>
                )}

                {!code && UUID_RX.test(deal.id) && (
                  <button type="button" className="wg-coupon__cta">
                    Save deal
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DealCouponModal;

function formatExpiresIn(d: Date): string {
  const diff = d.getTime() - Date.now();
  if (diff <= 0) return 'expired';
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  if (days > 0) return `in ${days}d ${hours}h`;
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}

function StoreIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m2 9 1-5h18l1 5" />
      <path d="M3 9v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9" />
    </svg>
  );
}
function TagIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12 12 3H3v9l9 9z" />
      <circle cx="7" cy="7" r="1.4" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="13" />
      <circle cx="12" cy="16.5" r="0.6" fill="currentColor" />
    </svg>
  );
}
